// poll-1 — "Engagement Poll" (organic, v1 port)
//
// The native-action poll: a question pinned at the top, one card per option in
// the middle of the frame, and a bottom CTA teaching the voting mechanic. No
// API and no poll sticker is involved — the vote IS the post's own engagement,
// so the tally is visible on the post itself.
//
// Renderdoc port of the v1 PollLayer
// (packages/remotion/src/components/poll-layer.tsx), built entirely from plain
// generic blocks:
//   - spine: procedure b-roll, full-bleed, looping, beat-synced cuts every 4
//     beats (template def `beatsPerEdit: 4`).
//   - spine: a `solid` carrying v1's 4-stop vertical scrim gradient verbatim
//     (the `solid` colour field accepts any CSS background string).
//   - overlay: the question as a single `text` block, top-centre, fading in
//     over v1's 12-frame intro window.
//   - overlay: a `group` in the centre — ONE `repeat` over the option list,
//     whose child is a full-width card carrying the row's ACTION VERB above the
//     option label. Rows accumulate (v1 rows never leave).
//   - overlay: v1's fixed mechanic CTA as a dark pill at the bottom.
//
// ─── WHY A `group` AND NOT A `staggered-list` ───────────────────────────────
// A poll row is not one styled string: it is a labelled, per-row-tinted card.
// `staggered-list` has exactly one style, one container and one kicker for
// EVERY item, so the two things that make the format work — the per-row verb
// and the accent fill on the primary option — were both inexpressible.
//
// `group` needs no new field for either. One `repeat` establishes ONE $index
// and the whole child subtree reads it:
//
//     repeat over body ──► $index = 0            $index = 1          $index = 2
//       stack (the card)   container[0] accent   container[1] paper  container[2] paper
//         text  (verb)     "Like"                "Comment"           "Share"
//         text  ($item)    likeLabel             commentLabel        shareLabel
//
// The verb, the label colours and the card fill are three independent
// `{ byIndex: [...] }` cycles that cannot drift apart, because there is only
// one index to read. Row adjacency (v1's whole point — the verb sits ON the
// row it votes for) is restored, and the CTA goes back to v1's copy.
//
// CONTENT CONTRACT — DECLARED, in the `content` block below:
//   question  → text
//   options   → a list of RECORDS, `{ action, label }`
//
// v1's `likeLabel` / `commentLabel` / `shareLabel` were three separately-typed
// fields; v2 flattened them into `body[0..2]`, where the row's meaning was its
// POSITION and nothing said so. A record row carries its own verb, so:
//   - the verb is generated WITH the label it belongs to and cannot drift;
//   - a third option is a third row, not a fourth `body` item that the
//     hardcoded CTA silently dropped;
//   - the count is bounded by the declaration (`min: 2, max: 3`) rather than by
//     prose in another package.
//
// `action` is a `choice`, not free text: the row's verb must be one of the
// three engagement actions Instagram actually offers, and the value is
// user-visible copy the template prints verbatim.
//
// v1 design source of truth (PollLayer):
//   - scrim: linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.24) 30%,
//     rgba(0,0,0,0.24) 62%, rgba(0,0,0,0.66) 100%)
//   - question: top padding 9.5%, maxWidth 86%, 58px / 800 / white,
//     lineHeight 1.14, textShadow '0 4px 24px rgba(0,0,0,0.55)', fades in over
//     frames 0–12
//   - vote rows: full-width cards, padding 24/30, radius 22, gap 24, horizontal
//     padding 9%; row 1 filled with the brand accent, rows 2–3 on `paper`;
//     box-shadow '0 10px 34px rgba(0,0,0,0.35)'; each springs in (damping 14 /
//     stiffness 150 / mass 0.7, 12 frames) at delays 8 / 14 / 20, scale 0.86→1
//   - inside each row: a 72×72 rounded icon chip (heart / comment bubble /
//     paper-plane), then a 25px / 800 / 0.16em uppercase action verb over a
//     42px / 800 option label
//   - CTA: fixed copy "Vote with your tap", 32px / 800 / 0.06em uppercase,
//     white on the solid slate pill (radius 999), bottom padding 12%, pulsing
//     ±3% on a 1s sine
//
// FIDELITY GAPS vs v1
//
// CLOSED by moving the rows onto `group`:
//   - the per-row ACTION VERB. It sits on its own row again — and it is now
//     CONTENT (`{source:'field', field:'action'}` off a declared record row),
//     not a `{ byIndex: [...] }` literal. That line matters: v1's verbs are
//     model-generated, so encoding them as authored constants would look right
//     for English poll videos and quietly reinstate the fixed-copy problem.
//     The CHIP STYLING per row stays `byIndex` — that is presentation, and it
//     cycles with the index because it is authored, not generated.
//   - the ACCENT FILL on row 1 (`container: { byIndex: [...] }` with
//     `backgroundRole: 'accent'` on index 0)
//   - the CTA copy: v1's "Vote with your tap" is back, because the CTA no
//     longer has to carry the mechanic
//   - full-width cards at v1's radius 22 / padding 24-30 / shadow (a Frame is
//     authored, not picked from `pill | button | plate`), and left-aligned rows
//   - brand colour: the filled row is bound to the theme `accent` role
//   - v1's tighter line-heights (option label 1.12, question 1.14) against the
//     engine's 1.25 default, via `TypeStyleOverride.lineHeight`
//
// DELIBERATELY NOT USED:
//   - `repeat.active` / `repeat.past`. They exist for a row that is CURRENT
//     right now (step-timer's advancing checklist). A poll has no current row:
//     v1 paints row 1 with the accent permanently, because it is the LIKE vote,
//     not because it is the row that just arrived. That is a positional look,
//     which `container: { byIndex: [...] }` already says correctly — a state
//     style would make the accent fill CHASE the cascade down the card and
//     leave the like row cream, which is wrong.
//
// STILL INEXPRESSIBLE:
//   - the per-row ACTION ICONS. v1 draws inline SVG (filled red heart, outline
//     comment bubble, paper-plane) in a rounded chip at the head of each row.
//     No node in the `group` tree renders vector art — the tree is stack /
//     text / bar — and there is no image node either, so the icon chip has no
//     expression at all. A row is therefore verb + label only. GAP FOR THE
//     MODEL: an `icon` (or `image`) node kind, whose glyph could itself be
//     `Indexed<…>` so one repeat cycles heart/bubble/plane the same way it
//     cycles the verb.
//   - text-on-accent CONTRAST PICKING. v1 runs `accentTextColor(accent)` so a
//     pale brand accent gets dark text. `colorRole` binds to fixed theme roles
//     (there is no `onAccent`, and no derived/computed colour), so the filled
//     row's text is pinned to `onPrimary` and a very light brand accent would
//     read poorly. Same for v1's `deepAccentColor(accent)` on the unfilled
//     verbs, approximated here by the plain `accent` role.
//   - Row entrances are one BEAT apart (`stagger.beatsPerItem` is an integer
//     count of beats; 1 ≈ 18 frames at 100bpm) instead of v1's 6-frame gaps
//     (delays 8 / 14 / 20). `beatsPerItem: 0` would fire them all together and
//     lose the cascade entirely, so 1 is the closest available.
//   - `pop` approximates v1's spring — same intent, but v2 owns the spring
//     constants (a node CAN set `entranceDurationFrames`, so v1's 12 frames is
//     exact; the damping/stiffness/mass are not).
//   - The CTA pill is still `container: 'button'` on a `text` block (#111,
//     radius 14) rather than v1's fully-round slate pill, and it does NOT
//     pulse — v2 has no looping/idle animation token, only entrances. (It
//     could be moved onto a `group` to author the round Frame, but a one-node
//     group buys only the radius and loses nothing else, so it stays a `text`.)
//   - The byIndex STYLE cycles are 3 long and would wrap on a 4th row (it would
//     be painted with the accent fill again). The row COUNT is now capped in
//     this file — `options.max: 3` — so a 4th row cannot be generated, and the
//     wrap is unreachable rather than merely unlikely. (The verb no longer
//     wraps at all: it comes from the row.)
const FPS = 30;
// Fallback length only (music-only template, no narration to drive it). The
// whole card is on screen by ~frame 40 and then holds; 10s is ~4 clip windows
// at beatsPerEdit 4.
const TOTAL_FRAMES = 10 * FPS;
// v1: `intro` interpolates frames 0→12 for the question and the CTA.
const INTRO_FRAMES = 12;
// v1 row geometry (VoteRow): padding '24px 30px', radius 22, gap 24.
const ROW_RADIUS = 22;
const ROW_PADDING_X = 30;
const ROW_PADDING_Y = 24;
const ROW_GAP = 24;
const ROW_SHADOW = '0 10px 34px rgba(0,0,0,0.35)';
// v1 REEL_PALETTE.paper — the unfilled row card.
const PAPER = 'rgba(250, 247, 242, 0.95)';
// v1 REEL_PALETTE.slate — the option label on a paper row.
const SLATE = '#15161C';
// v1 VoteRow type.
const VERB_FONT_SIZE = 25;
const LABEL_FONT_SIZE = 42;
// v1 option label span runs tighter than the engine's 1.25 default.
const LABEL_LINE_HEIGHT = 1.12;
// v1 rows spring in over 12 frames.
const ROW_ENTRANCE_FRAMES = 12;
export const poll1 = {
    id: 'poll-1',
    schemaVersion: 2,
    content: {
        question: {
            type: 'text',
            maxChars: 90,
            description: 'The poll question, pinned at the top of the frame. A genuine either/or ' +
                'the audience has an opinion about — not a rhetorical marketing line. ' +
                '≤10 words, sentence case, ends in a question mark.',
        },
        options: {
            type: 'list',
            min: 2,
            max: 3,
            description: 'The poll options, one card each, IN VOTING ORDER: the like option ' +
                'first, then the comment option, then (optionally) the share option. ' +
                'Each names the engagement ACTION the viewer takes to vote for it, so ' +
                'the vote is cast on the post itself — there is no poll sticker.',
            of: {
                action: {
                    type: 'choice',
                    options: ['Like', 'Comment', 'Share'],
                    description: 'The engagement action that casts a vote for this option. Use ' +
                        '"Like" for the first row, "Comment" for the second, "Share" for ' +
                        'the third. Printed verbatim above the label.',
                },
                label: {
                    type: 'text',
                    maxChars: 36,
                    description: 'What voting this way means — a concrete service, look or ' +
                        'preference the business actually offers. 1-4 words, Title Case, ' +
                        'no punctuation. E.g. "Glass skin facial", "Soft glam".',
                },
            },
        },
    },
    aspectRatios: ['portrait'],
    duration: { kind: 'fixed', frames: TOTAL_FRAMES },
    root: {
        kind: 'leaf',
        id: 'main',
        spine: [
            // Calm procedure / ambience b-roll — the poll card sits on top for the
            // whole video (v1 backgroundFootageHint), cutting every 4 beats.
            {
                kind: 'media-track',
                id: 'broll',
                duration: { kind: 'fill' },
                clips: {
                    source: 'query',
                    query: { kind: 'asset-clips', tag: 'procedure', count: [1, 4] },
                    required: true,
                },
                fit: 'contain-blur',
                cuts: { mode: 'beat-synced', beatsPerEdit: 4 },
                fillMode: 'loop',
            },
            // v1's scrim, copied verbatim — dark at the question and at the CTA,
            // near-flat through the middle where the option cards sit.
            {
                kind: 'solid',
                id: 'scrim',
                duration: { kind: 'fill' },
                color: {
                    source: 'fixed',
                    value: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.24) 30%, rgba(0,0,0,0.24) 62%, rgba(0,0,0,0.66) 100%)',
                },
            },
        ],
        overlays: [
            // The poll question — fades in once and holds for the whole clip.
            {
                kind: 'text',
                id: 'question',
                text: {
                    source: 'query',
                    query: { kind: 'content', field: 'question' },
                    required: true,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: 58,
                    fontWeight: 800,
                    textTransform: 'none',
                    letterSpacing: 0,
                    color: '#FFFFFF',
                    // v1 question block.
                    lineHeight: 1.14,
                    textShadow: '0 4px 24px rgba(0,0,0,0.55)',
                    strokeWidth: 0,
                },
                animation: {
                    entrance: 'fade-in',
                    entranceDurationFrames: INTRO_FRAMES,
                },
                // v1: paddingTop 9.5%, maxWidth 86%, centred.
                placement: { anchor: 'top', x: 0.5, y: 0.095, width: 0.86 },
                duration: { kind: 'fill' },
                container: 'none',
            },
            // The vote rows. ONE repeat, ONE index — the verb, the two text colours
            // and the card fill all read it, so a row can never be labelled "Like"
            // while being painted as the comment row.
            {
                kind: 'group',
                id: 'options',
                duration: { kind: 'fill' },
                // Dead-centre, at v1's 82% card column (horizontal padding 9%).
                placement: { anchor: 'center', x: 0.5, y: 0.5, width: 0.82 },
                root: {
                    kind: 'repeat',
                    over: {
                        source: 'query',
                        query: { kind: 'content', field: 'options' },
                        required: true,
                    },
                    direction: 'v',
                    // v1 stacks the rows with gap 24.
                    gap: ROW_GAP,
                    align: 'stretch',
                    // Closest available cascade — see FIDELITY GAPS.
                    stagger: { beatsPerItem: 1 },
                    // Every row stays on screen: the viewer must be able to compare the
                    // options in order to vote between them.
                    reveal: 'accumulate',
                    child: {
                        // The card itself. A stack (not a text) owns the Frame, so it
                        // stretches to the full 82% column the way v1's rows do instead of
                        // hugging its copy.
                        kind: 'stack',
                        direction: 'v',
                        // v1 stacks verb over label with gap 4.
                        gap: 4,
                        align: 'start',
                        container: {
                            byIndex: [
                                // Row 1 — the ❤️ like vote, filled with the brand accent so the
                                // primary option reads first (v1 `filled: true`).
                                {
                                    backgroundRole: 'accent',
                                    radius: ROW_RADIUS,
                                    paddingX: ROW_PADDING_X,
                                    paddingY: ROW_PADDING_Y,
                                    shadow: ROW_SHADOW,
                                },
                                // Rows 2–3 — warm paper.
                                {
                                    background: PAPER,
                                    radius: ROW_RADIUS,
                                    paddingX: ROW_PADDING_X,
                                    paddingY: ROW_PADDING_Y,
                                    shadow: ROW_SHADOW,
                                },
                                {
                                    background: PAPER,
                                    radius: ROW_RADIUS,
                                    paddingX: ROW_PADDING_X,
                                    paddingY: ROW_PADDING_Y,
                                    shadow: ROW_SHADOW,
                                },
                            ],
                        },
                        // v1: each row springs in from scale 0.86 over 12 frames.
                        entrance: 'pop',
                        entranceDurationFrames: ROW_ENTRANCE_FRAMES,
                        children: [
                            // The ACTION VERB — which of Instagram's own buttons casts this
                            // vote. Cycles with the row, so it is adjacent to the option it
                            // labels (v1's whole mechanic).
                            {
                                kind: 'text',
                                // GENERATED, not authored — the verb belongs to the row it
                                // labels, so it arrives with it.
                                content: { source: 'field', field: 'action' },
                                style: 'caption',
                                styleOverride: {
                                    byIndex: [
                                        // On the accent fill.
                                        {
                                            fontSize: VERB_FONT_SIZE,
                                            fontWeight: 800,
                                            letterSpacing: 0.16,
                                            textTransform: 'uppercase',
                                            colorRole: 'onPrimary',
                                            strokeWidth: 0,
                                        },
                                        // On paper — v1 uses deepAccentColor(accent); the plain
                                        // accent role is the nearest available (see GAPS).
                                        {
                                            fontSize: VERB_FONT_SIZE,
                                            fontWeight: 800,
                                            letterSpacing: 0.16,
                                            textTransform: 'uppercase',
                                            colorRole: 'accent',
                                            strokeWidth: 0,
                                        },
                                        {
                                            fontSize: VERB_FONT_SIZE,
                                            fontWeight: 800,
                                            letterSpacing: 0.16,
                                            textTransform: 'uppercase',
                                            colorRole: 'accent',
                                            strokeWidth: 0,
                                        },
                                    ],
                                },
                                align: 'left',
                            },
                            // The option label the viewer is voting for.
                            {
                                kind: 'text',
                                content: { source: 'field', field: 'label' },
                                style: 'heading',
                                styleOverride: {
                                    byIndex: [
                                        {
                                            fontSize: LABEL_FONT_SIZE,
                                            fontWeight: 800,
                                            textTransform: 'none',
                                            letterSpacing: 0,
                                            colorRole: 'onPrimary',
                                            lineHeight: LABEL_LINE_HEIGHT,
                                            strokeWidth: 0,
                                        },
                                        {
                                            fontSize: LABEL_FONT_SIZE,
                                            fontWeight: 800,
                                            textTransform: 'none',
                                            letterSpacing: 0,
                                            color: SLATE,
                                            lineHeight: LABEL_LINE_HEIGHT,
                                            strokeWidth: 0,
                                        },
                                        {
                                            fontSize: LABEL_FONT_SIZE,
                                            fontWeight: 800,
                                            textTransform: 'none',
                                            letterSpacing: 0,
                                            color: SLATE,
                                            lineHeight: LABEL_LINE_HEIGHT,
                                            strokeWidth: 0,
                                        },
                                    ],
                                },
                                align: 'left',
                            },
                        ],
                    },
                },
            },
            // The mechanic CTA — v1's own fixed copy, restored now that the rows
            // carry their own verbs.
            {
                kind: 'text',
                id: 'vote-cta',
                text: { source: 'fixed', value: 'Vote with your tap' },
                style: 'heading',
                styleOverride: {
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: 0.06,
                    textTransform: 'uppercase',
                    color: '#FFFFFF',
                    strokeWidth: 0,
                },
                animation: {
                    entrance: 'fade-in',
                    entranceDurationFrames: INTRO_FRAMES,
                },
                // v1: paddingBottom 12%.
                placement: { anchor: 'bottom', x: 0.5, y: 0.88 },
                duration: { kind: 'fill' },
                container: 'button',
            },
        ],
    },
    globals: {
        audio: {
            // Music only — v1 is narrationMode 'text_only' with
            // supportsAiVoiceover: false.
            music: { source: 'query', query: { kind: 'music' }, required: false },
        },
        // No captions: the card carries the message.
    },
};
//# sourceMappingURL=poll-1.js.map